import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import {
  ResearchProject,
  ResearchTrackBasic,
  researchApi,
} from "../../services/researchApi";

interface BulkAddToTrackButtonProps {
  selectedEntities: Array<{
    id: string;
    name: string;
    type: "organization" | "facility" | "project";
  }>;
  onSuccess?: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const BulkAddToTrackButton: React.FC<BulkAddToTrackButtonProps> = ({
  selectedEntities,
  onSuccess,
  disabled = false,
  disabledTooltip,
}) => {
  const { showNotification } = useNotification();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [projects, setProjects] = useState<ResearchProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedTrack, setSelectedTrack] = useState<string>("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dialogOpen) {
      fetchProjects();
    }
  }, [dialogOpen]);

  useEffect(() => {
    setSelectedTrack("");
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await researchApi.getProjects();
      const editableProjects = response.results.filter(
        (p) =>
          p.permission_level === "OWNER" || p.permission_level === "EDITOR",
      );
      setProjects(editableProjects);
    } catch (error) {
      showNotification("Failed to load projects", "error");
    }
  };

  const selectedProjectObj = projects.find((p) => p.id === selectedProject);
  const tracks: ResearchTrackBasic[] = selectedProjectObj
    ? selectedProjectObj.tracks.filter((t) => t.status === "active")
    : [];

  const handleOpen = () => {
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedProject("");
    setSelectedTrack("");
    setReason("");
  };

  const handleBulkAddToTrack = async () => {
    if (!selectedTrack || selectedEntities.length === 0) return;

    try {
      setLoading(true);

      const result = await researchApi.bulkAddEntitiesToTrack({
        track: selectedTrack,
        entities: selectedEntities.map((entity) => ({
          kg_entity_id: entity.id,
          kg_entity_type: entity.type,
        })),
        reason: reason.trim() || undefined,
      });

      const trackName =
        tracks.find((t) => t.id === selectedTrack)?.name || "track";

      if (result.created_count === result.total_requested) {
        showNotification(
          `Added ${result.created_count} entities to ${trackName}`,
          "success",
        );
      } else if (result.created_count > 0) {
        showNotification(
          `Added ${result.created_count} of ${result.total_requested} entities to ${trackName}. ${result.already_existed_count} already existed.`,
          "success",
        );
      } else {
        showNotification(
          `All ${result.total_requested} entities already exist in ${trackName}`,
          "info",
        );
      }

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        "Failed to add entities to track. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedTrackName =
    tracks.find((t) => t.id === selectedTrack)?.name || "";

  const isDisabled = disabled || selectedEntities.length === 0;
  const tooltipTitle = isDisabled
    ? disabledTooltip || "Select entities to add to a research track"
    : `Add ${selectedEntities.length} entities to a research track`;

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            startIcon={<PlaylistAddIcon />}
            variant="outlined"
            size="small"
            disabled={isDisabled}
            onClick={handleOpen}
          >
            Add to Track ({selectedEntities.length})
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: { backgroundColor: "#1e1e1e" },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PlaylistAddIcon />
            Add {selectedEntities.length} Entities to Research Track
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Selected entities summary */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected entities ({selectedEntities.length}):
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  maxHeight: 100,
                  overflowY: "auto",
                }}
              >
                {selectedEntities.map((entity) => (
                  <Chip
                    key={entity.id}
                    label={entity.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            {/* Project selection */}
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.name}
              value={selectedProjectObj || null}
              onChange={(_event, newValue) => {
                setSelectedProject(newValue?.id || "");
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  placeholder="Search projects..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.raised",
                    "& .MuiAutocomplete-option": {
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "rgba(25, 118, 210, 0.2)",
                      },
                    },
                  },
                },
              }}
            />

            {/* Track selection */}
            <Autocomplete
              options={tracks}
              getOptionLabel={(option) => option.name}
              value={tracks.find((t) => t.id === selectedTrack) || null}
              onChange={(_event, newValue) => {
                setSelectedTrack(newValue?.id || "");
              }}
              disabled={!selectedProject}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={
                selectedProject
                  ? "No active tracks in this project"
                  : "Select a project first"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Track"
                  placeholder="Search tracks..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "background.raised",
                    "& .MuiAutocomplete-option": {
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "rgba(25, 118, 210, 0.2)",
                      },
                    },
                  },
                },
              }}
            />

            {/* Reason */}
            <TextField
              label="Reason (optional)"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you adding these entities?"
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#1e1e1e" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleBulkAddToTrack}
            variant="contained"
            disabled={
              !selectedTrack || selectedEntities.length === 0 || loading
            }
          >
            {loading ? "Adding..." : `Add to ${selectedTrackName || "Track"}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkAddToTrackButton;
